import {
  useState, useRef, useEffect, useImperativeHandle, forwardRef,
} from 'react';
import {
  string, number, bool, shape, func,
} from 'prop-types';
import {
  Card,
  Input,
  Icon,
  Slider,
  Tooltip,
} from 'components/common';
import { isNumberInRange } from 'helpers/ValidationUtil';
import { patterns } from 'helpers/behaviors';

import './SliderPanel.scss';

const SliderPanel = forwardRef(({
  name: { display },
  notBordered,
  minValue,
  maxValue,
  prefix,
  tooltip,
  defaultValue,
  value: controlledValue,
  ...props
}, ref) => {
  /* Refs */
  const isModeControlledRef = useRef(
    (function () {
      const modeControlled = controlledValue !== undefined;
      const modeUncontrolled = defaultValue !== undefined;
      if (modeControlled && modeUncontrolled) {
        // eslint-disable-next-line
        console.error(`Warning: You shoud not specify both 'value' and 'defaultValue' at the same time.\n \
        To use controlled mode specify 'value' only,\n \
        To use uncontrolled mode you can optionally specify 'defaultValue'`);
      }
      return modeControlled;
    })()
  );

  /* State */
  const [uncontrolledValue, setUncontrolledValue] = useState(() => (
    isModeControlledRef.current ? null : defaultValue
  ));
  const value = isModeControlledRef.current ? controlledValue : uncontrolledValue;

  /* Expose */
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  });
  useImperativeHandle(ref, () => ({
    get value() {
      return valueRef.current;
    },
  }));

  /* Handlers */
  const handleInputFocus = (focusedValue) => {
    !isModeControlledRef.current && setUncontrolledValue(focusedValue);

    const { onFocus } = props;
    onFocus?.(focusedValue);
  };

  const handleInputChange = (newValue) => {
    !isModeControlledRef.current && setUncontrolledValue(newValue);

    const { onChange } = props;
    onChange?.(newValue);
  };

  const handleInputBlur = (blurredValue) => {
    !isModeControlledRef.current && setUncontrolledValue(blurredValue);

    const { onBlur } = props;
    onBlur?.(blurredValue);
  };

  const handleSliderChange = (newValue) => {
    const { onChange } = props;

    isModeControlledRef.current
      ? onChange?.(newValue)
      : setUncontrolledValue(newValue);
  };

  const handleSliderAfterChange = (newValue) => {
    const { onChange } = props;

    !isModeControlledRef.current && onChange?.(newValue);
  };

  return (
    <Card.Content notBordered={notBordered} className="slider-panel" dataSelectorId={props.queryParam} dataSelectorType="slider-panel">
      <Input
        dataSelectorId={props.queryParam}
        title={display}
        leftText={prefix}
        value={value}
        behaviors={patterns.readableNaturalRange({
          minValue,
          maxValue,
          noDigits: `${maxValue}`.length,
          autoCorrect: true,
        })}
        type="tel"
        validate={v => (isNumberInRange(v, minValue, maxValue)
          ? ''
          // This message will not be displayed
          // It is only used to trigger error response
          : 'error')}
        errorComponent={null}
        showSuccessIcon={false}
        showFeedbackOnce="dirty"
        onFocus={handleInputFocus}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      />
      <Slider
        minValue={minValue}
        maxValue={maxValue}
        value={value}
        onChange={handleSliderChange}
        onAfterChange={handleSliderAfterChange}
        tabIndex={-1}
      />
      {tooltip.display && (
        <Tooltip id="slider-panel-info" text={tooltip.display} position="top">
          <Icon name="info" secondary small />
        </Tooltip>
      )}
    </Card.Content>
  );
});

SliderPanel.displayName = 'SliderPanel';

SliderPanel.defaultProps = {
  notBordered: false,
  minValue: 0,
  maxValue: Number.MAX_SAFE_INTEGER,
  name: {
    display: '',
  },
  tooltip: {
    display: '',
    key: '',
  },
};

SliderPanel.propTypes = {
  name: shape({
    display: string,
  }),
  notBordered: bool,
  defaultValue: number,
  value: number,
  minValue: number,
  maxValue: number,
  step: number.isRequired,
  onChange: func,
  prefix: string,
  tooltip: shape({
    display: string,
    key: string,
  }),
  onFocus: func,
  onBlur: func,
  queryParam: string,
};

export default SliderPanel;
